import React from 'react';
import { Button, FallbackImage, Select } from 'components';
import { IPlan, IPlanForm } from './types';
import { Constants, Globals } from 'utils';
import { CheckWhiteIcon, CloseIcon } from 'assets/icons';
import { PlanService } from 'services';
import { WhiteImage } from "assets/img";

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  item?: IPlan;
}

interface IState {
  form: IPlanForm & { content: string[] };
  submitting: boolean;
}

const AFFILIATED_TYPES = [
  { value: Constants.AFFILIATE_TYPES.MEDIC.toString(), label: 'Médico Veterinario' },
  { value: Constants.AFFILIATE_TYPES.CLINIC.toString(), label: 'Clínica Veterinaria' },
  { value: Constants.AFFILIATE_TYPES.BUSINESS_SERVICE_OR_ADVERTISER.toString(), label: 'Comercio, servicio o anunciante' },
];

const SERVICES = [
  { value: '1', label: 'Telecomunicaciones' },
  { value: '2', label: 'Chat' },
  { value: '3', label: 'Afiliaciones y pagos' },
  { value: '4', label: 'Servicios y productos' },
  { value: '5', label: 'Publicidad contratada' },
  { value: '6', label: 'Historial afiliaciones' },
];

class ModalCreatePlan extends React.Component<Props, IState> {
  state: IState = {
    form: {
      plan_id: undefined,
      name: '',
      affiliate_type: '',
      icon: '',
      icon_blob: '',
      amount: '',
      mensual_amount: '',
      trimestral_amount: '',
      semestral_amount: '',
      services: [],
      content: [],
    } as any,
    submitting: false,
  }

  componentDidMount(): void {
    if (this.props.item) {
      this.fillForm();
    }
  }

  fillForm = () => {
    this.setState({
      form: {
        ...this.state.form,
        plan_id: this.props.item?.id || '',
        name: this.props.item?.name || '',
        affiliate_type: (this.props.item?.affiliate_type || ''),
        icon: this.props.item?.icon || '',
        icon_blob: this.props.item?.icon ? Globals.fromPhotos(this.props.item?.icon) : '',
        amount: this.props.item?.amount || '',
        mensual_amount: this.props.item?.mensual_amount || '',
        trimestral_amount: this.props.item?.trimestral_amount || '',
        semestral_amount: this.props.item?.semestral_amount || '',
        services: [],
        content: Array.isArray(this.props.item?.content) ? [...(this.props.item?.content || [])] : [],
      } as any,
    });
  }

  selectPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    this.change('icon', file as any);
    this.change('icon_blob', URL.createObjectURL(file) as any);
    e.target.value = '';
  }

  change = <T extends keyof IState['form'], U extends IState['form'][T]>(field: T, value: U) => {
    this.setState((s: IState) => ({ form: { ...s.form, [field]: value } }));
  }

  toggleService = (value: string) => {
    const services = Globals.clone(this.state.form.services);
    const index = services.findIndex((i: string) => i === value);

    if (index === -1) {
      services.push(value);
    } else {
      services.splice(index, 1);
    }

    this.change('services', services as any);
  }

  addContentField = () => {
    this.change('content', [...this.state.form.content, ''] as any);
  }

  removeContentField = (index: number) => {
    const newContent = [...this.state.form.content];
    newContent.splice(index, 1);
    this.change('content', newContent as any);
  }

  handleContentChange = (index: number, value: string) => {
    const newContent = [...this.state.form.content];
    newContent[index] = value;
    this.change('content', newContent as any);
  }

  isValidForm = () => {
    const onError = (msg: string) => {
      Globals.showError(msg);
      return false;
    }

    if (!this.state.form.affiliate_type)
      return onError('El campo tipo de afiliado es requerido');

    if (!this.state.form.name.trim().length)
      return onError('El campo nombre es requerido');

    if (Number(this.state.form.amount) <= 0)
      return onError('El campo tarifa anual es requerido');

    if (Number(this.state.form.semestral_amount) <= 0)
      return onError('El campo tarifa semestral es requerido');

    if (Number(this.state.form.trimestral_amount) <= 0)
      return onError('El campo Tarifa trimestral es requerido');

    if (Number(this.state.form.mensual_amount) <= 0)
      return onError('El campo Tarifa mensual es requerido');

    return true;
  }

  submit = async (e: any) => {
    e.preventDefault();
    if (!this.isValidForm()) return;

    this.setState({ submitting: true });
    try {
      const cleanedContent = this.state.form.content.filter(str => str.trim().length > 0);
      const payload = { ...this.state.form, content: cleanedContent, hasFile: true };

      this.props.item
        ? await PlanService.admin.update(payload)
        : await PlanService.admin.create(payload);

      this.props.onSubmit();
    } finally {
      this.setState({ submitting: false });
    }
  }

  render() {
    const { form } = this.state;

    return (
      <div id="modal-create-plan">
        <form onSubmit={this.submit}>
          <h3>{!this.props.item ? 'Nuevo' : 'Editar'} plan</h3>

          <label className="photo-field">
            <FallbackImage
              src={form.icon_blob}
              placeholder={WhiteImage}
              className="plan-icon"
              placeholderClassName="plan-icon-placeholder"
            />
            <input type="file" hidden accept="image/jpeg,image/png" onChange={this.selectPhoto} />
          </label>

          <Select
            label="Tipo de afiliados"
            name="client_type"
            options={AFFILIATED_TYPES}
            value={form.affiliate_type}
            onChange={e => this.change('affiliate_type', e.target.value as any)}
          />

          <Input
            label="Nombre del plan"
            value={form.name}
            onChange={e => this.change('name', e.target.value as any)}
          />

          <div className="custom-input">
            <p className="label">Precio de Tarifas</p>
            <p className="sub-label">Tarifa anual</p>
            <Input
              type="number"
              prepend="$"
              value={form.amount}
              onChange={e => this.change('amount', Globals.sanitizeToDecimal(e.target.value) as any)}
            />
          </div>

          <div className="fees">
            <div className="group-row">
              <p>Tarifa semestral</p>
              <Input
                type="number"
                prepend="$"
                value={form.semestral_amount}
                onChange={e => this.change('semestral_amount', Globals.sanitizeToDecimal(e.target.value) as any)}
              />
            </div>

            <div className="group-row">
              <p>Tarifa trimestral</p>
              <Input
                type="number"
                prepend="$"
                value={form.trimestral_amount}
                onChange={e => this.change('trimestral_amount', Globals.sanitizeToDecimal(e.target.value) as any)}
              />
            </div>

            <div className="group-row">
              <p>Tarifa mensual</p>
              <Input
                type="number"
                prepend="$"
                value={form.mensual_amount}
                onChange={e => this.change('mensual_amount', Globals.sanitizeToDecimal(e.target.value) as any)}
              />
            </div>
          </div>

          <div className="plan-benefits-section">
            <p className="benefits-title">Beneficios del Plan (Contenido)</p>

            {form.content.map((item, index) => (
              <div key={`content-${index}`} className="benefit-item">
                <Input
                  value={item}
                  placeholder={`Beneficio ${index + 1}`}
                  onChange={(e) => this.handleContentChange(index, e.target.value)}
                  append={
                    <button
                      type="button"
                      className="btn-delete-benefit"
                      onClick={() => this.removeContentField(index)}
                      title="Eliminar beneficio"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  }
                />
              </div>
            ))}

            {form.content.length === 0 && (
              <p className="empty-msg">No hay beneficios agregados.</p>
            )}
            <button
              type="button"
              onClick={this.addContentField}
              style={{
                backgroundColor: 'transparent',
                color: '#758640',
                border: '1px solid #758640',
                borderRadius: '6px',
                fontFamily: 'Raleway-Bold, sans-serif',
                fontSize: '13px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'center',
                padding: '8px 0',
                display: 'block',
                margin: '15px auto 10px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(117, 134, 64, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              + Añadir beneficio
            </button>
          </div>

          <div className="row row-buttons" style={{ marginTop: '30px' }}>
            <div className="col-md-6 text-center">
              <Button type="button" onClick={this.props.onClose}>
                Cerrar
              </Button>
            </div>
            <div className="col-md-6 text-center">
              <Button type="submit">
                Guardar
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

type CheckboxProps = {
  label: string
  isActive: boolean
  onChange: (value: boolean) => void
}

const Checkbox = ({ label, isActive, onChange }: CheckboxProps) => (
  <div onClick={() => onChange(!isActive)}>
    <div className={Globals.classNames('checkbox', isActive ? 'active' : '')}>
      {isActive && (
        <img src={CheckWhiteIcon} alt="Checkbox" />
      )}
    </div>
    <span>{label}</span>
  </div>
)

type InputProps = {
  label?: string
  type?: string
  prepend?: string | React.JSX.Element
  append?: string | React.JSX.Element
  value: string | number
  placeholder?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const Input = ({ label, type = 'text', prepend, append, value, placeholder, onChange }: InputProps) => {
  return (
    <div className="input-base">
      {!!label && (
        <label>{label}</label>
      )}
      <div className="input-wrapper">
        {!!prepend && (
          <div className="input-prepend">{prepend}</div>
        )}
        <input
          type={type}
          value={String(value || '')}
          placeholder={placeholder}
          onChange={onChange}
        />
        {!!append && (
          <div className="input-append">{append}</div>
        )}
      </div>
    </div>
  )
}

export default ModalCreatePlan;